import * as React from 'react';
import AIProcessingStages from './AIProcessingStages';
import { AIHeader } from './AIHeader';
import {
    DataCollectionAiAgentOperationState,
    DataCollectionAiAgentOperationCode,
    DataCollectionAiAgentOperationType
} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import {
    isTerminatedAiOperationState,
    parseAiOperationStateValue
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer } from './hooks/useLocalizer';
import { AITerminateButton } from './AITerminateButton';
import { useAiActionTarget } from './hooks/useAiActionTarget';

const emailNoticeIconSrc = "/images/supplier-ai-agent/email-notice-icon.svg";

interface AIProcessingStateProps {
    isProcessing: boolean;
    processingProgress: number;
    currentStage?: number;
    localizer?: IStringLocalizer;
    onError?: (message: string) => void;
    operationType: DataCollectionAiAgentOperationType;
}

export const AIProcessingState: React.FC<AIProcessingStateProps> = ({
    isProcessing,
    processingProgress,
    currentStage = 0,
    localizer,
    onError,
    operationType
}) => {
    const localize = useLocalizer(localizer);
    const processingStateRef = React.useRef<HTMLDivElement>(null);
    const { aiOperation } = useAiActionTarget(operationType);

    const aiState = parseAiOperationStateValue(aiOperation?.state);
    const agentOperationCode = (aiOperation?.agentOperationCode ?? null) as DataCollectionAiAgentOperationCode | null;
    const agentState = aiOperation?.agentState || null;
    const queueTasksAhead = aiOperation?.queueTasksAhead ?? null;

    /**
     * Effect: Scroll to processing state when processing starts
     */
    React.useEffect(() => {
        if (isProcessing && processingStateRef.current) {
            setTimeout(() => {
                if (processingStateRef.current) {
                    const uatBanner = document.getElementById('uat-banner');
                    const bannerHeight = uatBanner && uatBanner.offsetHeight > 0
                        ? uatBanner.offsetHeight
                        : 0;
                    const offset = bannerHeight + 20;

                    if (bannerHeight > 0) {
                        const elementRect = processingStateRef.current.getBoundingClientRect();
                        const absoluteElementTop = elementRect.top + window.pageYOffset;
                        const targetPosition = absoluteElementTop - offset;

                        window.scrollTo({
                            top: targetPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        processingStateRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                }
            }, 100);
        }
    }, [isProcessing]);

    if (!isProcessing) {
        return null;
    }

    // Don't show custom error state - let parent component handle failure display
    if (aiState === DataCollectionAiAgentOperationState.Failed || isTerminatedAiOperationState(aiState)) {
        return null;
    }

    const isDeleteOperation = aiState === DataCollectionAiAgentOperationState.DeleteInProgress
        || aiState === DataCollectionAiAgentOperationState.DeleteCompleted;
    const canTerminateOperation = aiState === DataCollectionAiAgentOperationState.Triggered
        || aiState === DataCollectionAiAgentOperationState.RequestInProgress;

    return (
        <div ref={processingStateRef} className="ai-processing">
            <AIHeader localizer={localizer} />
            <div className="ai-processing-stages-container">
                <AIProcessingStages 
                    currentStage={currentStage} 
                    aiState={aiState} 
                    agentOperationCode={agentOperationCode}
                    agentState={agentState} 
                    queueTasksAhead={queueTasksAhead}
                    localizer={localizer} 
                />
            </div>

            {!isDeleteOperation && (
                <div className="ai-email-notice">
                    <img src={emailNoticeIconSrc} width="16" height="16" aria-hidden="true" alt="" />
                    <span>{localize("We'll email you when processing is complete")}</span>
                </div>
            )}

            <div>
                {!isDeleteOperation && (
                    <p className="ai-time-estimate" style={{ marginBottom: '3%' }}>
                        {localize("This may take 10 minutes to a few hours depending on survey complexity and document analysis. You may safely leave the page.")}
                    </p>
                )}
                {!isDeleteOperation && canTerminateOperation && (
                    <div className="ai-processing-actions">
                        <AITerminateButton
                            onError={onError}
                            localizer={localizer}
                            operationType={operationType}
                        />
                    </div>
                )}
                <div className="ai-survey-lock-notice">
                    <div className="alert alert-info" style={{ marginBottom: '0' }}>
                        <i className="glyphicon glyphicon-lock" style={{ marginRight: '8px' }}></i>
                        <strong>{localize("Survey Locked:")} </strong>
                        {isDeleteOperation
                            ? localize("All survey form fields are locked while AI answers are being deleted.")
                            : localize("All survey form fields are locked during AI processing for data integrity.")}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIProcessingState;
